import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { listPlaces } from '@/api/services/placesService';
import type { PlaceDto, PlaceType } from '@/api/types/Place';
import { PLACE_TYPES } from '@/api/types/Place';
import { TableSkeleton } from '@/components/loading';
import { Roles } from '@/constants/roles';
import { useAppSelector } from '@/store/hooks';
import { selectPlacesRevision } from '@/store/placesSlice';
import { selectUserRoles } from '@/store/userSlice';

import { OrdersViewToggle } from '../calendar/OrdersViewToggle';
import { NEW_PLACE_ID, PlaceEditor } from './PlaceEditor';
import { PhoneText } from './PhoneText';
import { PlacesCards } from './PlacesCards';
import { PlacesEmptyState } from './PlacesEmptyState';
import { PlaceTypeChip } from './PlaceTypeChip';
import { usePlaceCopy } from './usePlaceCopy';

// CHR-06 §2 — „Adresář", the third view next to Seznam and Kalendář. Shared
// single-level registry: every dispatcher and backoffice user sees the same
// rows. Server-side search + type filter + offset paging; the list is
// component-local state (see `placesSlice` for why).
//
// Fidelity anchor: `docs/change-requests/CHR-06-databaze-adres/prototyp.html`
// `#s-db` (toolbar · filters · card+table · `.tbl-foot` · `.legend`),
// `#s-empty` (empty state) and `#s-edit` (the dialog, in `PlaceEditor`).
// Below `md` the page swaps to a SEPARATE card tree (`PlacesCards`) — never a
// collapsed table.

// 250 ms — the needle is a SERVER query over name ∨ address ∨ contact ∨ note,
// so keystrokes are debounced into a single request.
const SEARCH_DEBOUNCE_MS = 250;

const PLACES_TABLE_COLUMN_WIDTHS = ['20%', '9%', '22%', '19%', '20%', '10%'];
const PLACES_TABLE_COLSPAN = PLACES_TABLE_COLUMN_WIDTHS.length;
const PLACES_SKELETON_ROWS = 6;

const BAR_GAP_FACTOR = 1.5;
const BAR_PAD_Y_FACTOR = 1.5;
const TAP_TARGET_MIN_PX = 44;
// Prototype `.filt.grow` inside a `max-width:380px` wrapper. Layout geometry,
// not a spacing-scale value.
const SEARCH_MIN_WIDTH_PX = 220;
const SEARCH_MAX_WIDTH_PX = 380;
const TYPE_FILTER_MIN_WIDTH_PX = 170;
const TYPE_COLUMN_WIDTH_PX = 100;
const ACTIONS_COLUMN_WIDTH_PX = 160;
const FOOTER_FONT_SIZE = 12;
const SORT_GLYPH_FONT_SIZE = 12;

const TYPE_ALL = 'all' as const;
type TypeFilter = PlaceType | typeof TYPE_ALL;

type ListStatus = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error';

// Defensive de-dupe on the append path: the api pages a LIVE table, so a row
// inserted (or renamed) between two requests can shift across the page
// boundary and arrive twice. First occurrence wins — the order stays the
// server's `LOWER(name) ASC, id ASC`.
const dedupeById = (list: PlaceDto[]): PlaceDto[] => {
  const seen = new Set<string>();
  const out: PlaceDto[] = [];
  for (const place of list) {
    if (seen.has(place.id)) continue;
    seen.add(place.id);
    out.push(place);
  }
  return out;
};

export const PlacesPage = (): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');
  const { enqueueSnackbar } = useSnackbar();
  // Desktop table vs. mobile cards are TWO TREES (CLAUDE.md FE-fidelity §9);
  // `down('md')` is the app-wide compact breakpoint (`OrdersPage`).
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const myRoles = useAppSelector(selectUserRoles);
  // `viewer` (CHR-03 §2 / ADR-0046) — app-wide read-only: no „Nová adresa",
  // no per-row „Upravit". Server-enforced too (writes 403).
  const canWrite = !myRoles.includes(Roles.VIEWER);
  // Bumped by every place mutation (`placesSlice`) — re-runs the fetch.
  const revision = useAppSelector(selectPlacesRevision);

  // Raw input vs. the debounced needle actually sent to the api.
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(TYPE_ALL);

  const [rows, setRows] = useState<PlaceDto[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<ListStatus>('idle');
  // A failed „Načíst další" must NOT blank the rows already on screen — it is
  // reported inline in the footer instead of the full-pane error.
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorId, setEditorId] = useState<string | null>(null);

  const { copiedId, copyPlace } = usePlaceCopy();

  // The highest offset page currently in `rows`. A REF, not state: the page is
  // an argument of the fetch (explicit replace/append), never an input that
  // re-triggers it — the earlier state version re-fetched the CURRENT page on
  // every invalidation and appended it onto itself (duplicate rows, and a
  // soft-deleted row stayed visible).
  const pageRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => {
      setQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Stale-response guard: a new needle fires a request while an older one may
  // still be in flight, and the api can answer out of order. Only the latest
  // request may write state — the `reqIdRef` pattern from
  // `RegistryAutocomplete`.
  const reqIdRef = useRef(0);
  // Re-entrancy guard for „Načíst další" (a double click must not fetch the
  // same offset page twice).
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(
    async (targetPage: number, mode: 'replace' | 'append'): Promise<void> => {
      if (mode === 'append' && inFlightRef.current) return;
      const reqId = ++reqIdRef.current;
      inFlightRef.current = true;
      setStatus(mode === 'replace' ? 'loading' : 'loadingMore');
      setLoadMoreFailed(false);
      try {
        const data = await listPlaces({
          ...(query ? { query } : {}),
          ...(typeFilter === TYPE_ALL ? {} : { type: typeFilter }),
          page: targetPage,
        });
        if (reqId !== reqIdRef.current) return;
        setRows((prev) =>
          mode === 'replace' ? data.content : dedupeById([...prev, ...data.content]),
        );
        setTotal(data.totalElements);
        pageRef.current = targetPage;
        setStatus('ready');
      } catch {
        // Covers the api being unreachable / 404 / 5xx.
        if (reqId !== reqIdRef.current) return;
        if (mode === 'append') {
          // Keep what is already loaded; offer a retry in the footer.
          setLoadMoreFailed(true);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      } finally {
        // Only the LATEST request may release the re-entrancy guard — a stale
        // one finishing after a newer fetch started would otherwise unlock the
        // „Načíst další" path while that newer request is still in flight.
        if (reqId === reqIdRef.current) inFlightRef.current = false;
      }
    },
    [query, typeFilter],
  );

  // Needle / type / invalidation all restart at page 0 and REPLACE: appending
  // page 1 of a different result set onto page 0 of the old one would
  // interleave two lists.
  useEffect(() => {
    void fetchPage(0, 'replace');
    // Invalidate whatever is in flight on unmount / before the next run, so a
    // late response cannot write into an unmounted (or restarted) list.
    return () => {
      reqIdRef.current += 1;
    };
    // `revision` is an explicit invalidation trigger, not a value `fetchPage`
    // closes over.
  }, [fetchPage, revision]);

  const hasMore = rows.length < total;
  const isLoadingAny = status === 'loading' || status === 'loadingMore';
  const activeType = typeFilter === TYPE_ALL ? null : typeFilter;

  // The editor edits a row from the CURRENT page — no extra GET by id.
  const editorTarget = useMemo(
    () =>
      editorId !== null && editorId !== NEW_PLACE_ID
        ? (rows.find((p) => p.id === editorId) ?? null)
        : null,
    [rows, editorId],
  );

  const openEditor = (id: string): void => {
    setSelectedId(id === NEW_PLACE_ID ? null : id);
    setEditorId(id);
  };

  const handleCopy = (place: PlaceDto): void => {
    setSelectedId(place.id);
    void copyPlace(place);
  };

  const clearFilters = (): void => {
    setSearchInput('');
    setTypeFilter(TYPE_ALL);
  };

  const emptyState = (
    <PlacesEmptyState
      typeFilter={activeType}
      hasQuery={query.length > 0}
      canWrite={canWrite}
      onClearFilter={clearFilters}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
      data-testid="places-page"
    >
      {/* Toolbar (prototype `.otoolbar`) — the three-item view switch LEFT,
          „Nová adresa" RIGHT, `space-between`. Identical row to OrdersPage /
          CalendarPage so the three views read as one system. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          pb: theme.spacing(BAR_PAD_Y_FACTOR),
          flexWrap: 'wrap',
          gap: theme.spacing(BAR_GAP_FACTOR),
        }}
      >
        <OrdersViewToggle value="addresses" stretchOnCompact />
        {canWrite ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openEditor(NEW_PLACE_ID)}
            data-testid="new-place-button"
            sx={{ minHeight: TAP_TARGET_MIN_PX }}
          >
            {t('action.newPlace')}
          </Button>
        ) : null}
      </Stack>

      {/* Filters row (prototype `.filters`) — search + Typ. An ACTIVE type
          filter takes the primary outline, exactly as `#s-empty` shows it. */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          pb: theme.spacing(BAR_PAD_Y_FACTOR),
          flexWrap: 'wrap',
          gap: theme.spacing(BAR_GAP_FACTOR),
        }}
      >
        <TextField
          size="small"
          placeholder={t('toolbar.search')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            flex: 1,
            minWidth: SEARCH_MIN_WIDTH_PX,
            maxWidth: SEARCH_MAX_WIDTH_PX,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'data-testid': 'places-search',
            'aria-label': t('toolbar.searchAria'),
          }}
        />
        <TextField
          select
          size="small"
          label={t('toolbar.type')}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          sx={{
            minWidth: TYPE_FILTER_MIN_WIDTH_PX,
            ...(activeType !== null
              ? {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '& .MuiInputBase-input': { color: theme.palette.primary.main },
                }
              : {}),
          }}
          inputProps={{ 'data-testid': 'places-type-filter' }}
        >
          {/* Vše · Nakládka · Vykládka · Obojí. „Vše" sends NO `type` param;
              every other option sends the enum verbatim. „Obojí" (`BOTH`) is
              an EXACT match server-side — the answer to „která místa umí
              obojí?" — while Nakládka/Vykládka stay inclusive of BOTH. */}
          <MenuItem value={TYPE_ALL}>{t('toolbar.typeAll')}</MenuItem>
          {PLACE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`type.${type}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: theme.spacing(2) }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void fetchPage(0, 'replace')}
            >
              {t('list.retry')}
            </Button>
          }
          data-testid="places-error"
        >
          {t('list.loadFailed')}
        </Alert>
      ) : isCompact ? (
        // MOBILE / TABLET — separate card tree, no table markup at all.
        <PlacesCards
          places={rows}
          status={status}
          canEdit={canWrite}
          onEdit={openEditor}
          emptyState={emptyState}
        />
      ) : (
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3, // 12px (DESIGN.md §4 card radius)
            boxShadow: theme.elevation.shadow1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Stack
                      component="span"
                      direction="row"
                      alignItems="center"
                      gap={0.25}
                    >
                      {t('column.name')}
                      {/* Sort is server-fixed `LOWER(name) ASC` in v1 — the
                          glyph states it, it is not a control. */}
                      <ArrowUpwardIcon
                        aria-hidden
                        sx={{ fontSize: SORT_GLYPH_FONT_SIZE }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: TYPE_COLUMN_WIDTH_PX }}>
                    {t('column.type')}
                  </TableCell>
                  <TableCell>{t('column.address')}</TableCell>
                  <TableCell>{t('column.contact')}</TableCell>
                  <TableCell>{t('column.note')}</TableCell>
                  <TableCell align="right" sx={{ width: ACTIONS_COLUMN_WIDTH_PX }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {/* FIRST load only (mirrors OrdersTable). On a refetch the
                    current rows STAY on screen — appending shimmer rows below
                    live data reads as „more rows are coming", which is a lie
                    when the page is simply re-querying the same 50. */}
                {status === 'loading' && rows.length === 0 ? (
                  <TableSkeleton
                    rows={PLACES_SKELETON_ROWS}
                    columnWidths={PLACES_TABLE_COLUMN_WIDTHS}
                  />
                ) : null}
                {rows.map((place) => {
                  const isCopied = copiedId === place.id;
                  return (
                    <TableRow
                      key={place.id}
                      hover
                      selected={selectedId === place.id}
                      data-testid={`place-row-${place.id}`}
                      data-type={place.type}
                      sx={{
                        '&.Mui-selected, &.Mui-selected:hover': {
                          bgcolor: theme.palette.primary.light,
                        },
                        '&.Mui-selected td:first-of-type': {
                          boxShadow: `inset 2px 0 0 0 ${theme.palette.primary.main}`,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{place.name}</TableCell>
                      <TableCell>
                        <PlaceTypeChip
                          type={place.type}
                          data-testid={`place-type-${place.id}`}
                        />
                      </TableCell>
                      <TableCell data-testid={`place-address-${place.id}`}>
                        {place.address?.trim() ? (
                          place.address
                        ) : (
                          <Box
                            component="span"
                            sx={{ color: theme.palette.text.disabled }}
                          >
                            {t('value.addressEmpty')}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <PhoneText
                          text={place.contact}
                          data-testid={`place-contact-${place.id}`}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ color: theme.palette.text.secondary, fontSize: 12.5 }}
                      >
                        <PhoneText
                          text={place.note}
                          data-testid={`place-note-${place.id}`}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Stack
                          direction="row"
                          gap={0.25}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          {/* Primary row action (prototype `.iconbtn`) — flips
                              to a check for 2 s after a successful copy. */}
                          <IconButton
                            onClick={() => handleCopy(place)}
                            aria-label={t(isCopied ? 'action.copyDone' : 'action.copy')}
                            title={t(isCopied ? 'action.copyDone' : 'action.copy')}
                            data-testid={`place-copy-${place.id}`}
                            data-copied={isCopied ? 'true' : 'false'}
                            sx={{
                              width: TAP_TARGET_MIN_PX,
                              height: TAP_TARGET_MIN_PX,
                              borderRadius: 1, // 8px — prototype `.iconbtn`
                              color: isCopied
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                              bgcolor: isCopied
                                ? theme.palette.primary.light
                                : 'transparent',
                              '&:hover': {
                                bgcolor: theme.palette.primary.light,
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            {isCopied ? (
                              <CheckIcon fontSize="small" />
                            ) : (
                              <ContentCopyOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                          {canWrite ? (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => openEditor(place.id)}
                              data-testid={`place-edit-${place.id}`}
                              sx={{ minHeight: TAP_TARGET_MIN_PX }}
                            >
                              {t('action.edit')}
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {status === 'ready' && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={PLACES_TABLE_COLSPAN} sx={{ p: 0 }}>
                      {emptyState}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer (prototype `.tbl-foot`) — count LEFT, „Načíst další" for the
              next offset page, sort statement RIGHT. */}
          {status !== 'loading' && rows.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              gap={theme.spacing(1.5)}
              sx={{
                px: theme.spacing(2),
                py: theme.spacing(1.25),
                borderTop: `1px solid ${theme.palette.divider}`,
                fontSize: FOOTER_FONT_SIZE,
                color: theme.palette.text.secondary,
                flexWrap: 'wrap',
              }}
              data-testid="places-footer"
            >
              <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
                {hasMore
                  ? t('list.shownOfTotal', { shown: rows.length, total })
                  : t('list.shown', { count: rows.length })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {loadMoreFailed ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={theme.spacing(1)}
                  data-testid="places-load-more-error"
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: FOOTER_FONT_SIZE,
                      color: theme.palette.error.main,
                    }}
                  >
                    {t('list.loadMoreFailed')}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => void fetchPage(pageRef.current + 1, 'append')}
                    disabled={isLoadingAny}
                    data-testid="places-load-more-retry"
                    sx={{ minHeight: TAP_TARGET_MIN_PX }}
                  >
                    {t('list.retry')}
                  </Button>
                </Stack>
              ) : hasMore ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => void fetchPage(pageRef.current + 1, 'append')}
                  disabled={isLoadingAny}
                  data-testid="places-load-more"
                  sx={{ minHeight: TAP_TARGET_MIN_PX }}
                >
                  {t('action.loadMore')}
                </Button>
              ) : null}
              <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
                {t('list.sortedBy')}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      )}

      {/* Legenda (prototype `.legend`) — what the two chips MEAN and what the
          clickable phone / the check glyph do. Explanatory chrome. */}
      {status === 'error' ? null : (
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={{
            gap: theme.spacing(2.25),
            mt: theme.spacing(1.75),
            px: theme.spacing(1.75),
            py: theme.spacing(1.5),
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1, // 8px (DESIGN.md §4 control radius)
            fontSize: FOOTER_FONT_SIZE,
            color: theme.palette.text.secondary,
          }}
          data-testid="places-legend"
        >
          <Stack direction="row" alignItems="center" gap={0.875}>
            <PlaceTypeChip type="LOADING" />
            <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
              {t('legend.loading')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.875}>
            <PlaceTypeChip type="UNLOADING" />
            <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
              {t('legend.unloading')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.875}>
            <Box
              component="span"
              aria-hidden
              sx={{
                fontFamily: theme.monospaceFontFamily,
                color: theme.palette.primary.main,
                textDecoration: 'underline dotted',
                textUnderlineOffset: '2px',
              }}
            >
              {t('legend.phoneSample')}
            </Box>
            <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
              {t('legend.phone')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.875}>
            <Box
              component="span"
              aria-hidden
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 1,
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.main,
              }}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
            </Box>
            <Typography component="span" sx={{ fontSize: FOOTER_FONT_SIZE }}>
              {t('legend.copied')}
            </Typography>
          </Stack>
        </Stack>
      )}

      <PlaceEditor
        placeId={editorId}
        existing={editorTarget}
        onClose={() => setEditorId(null)}
        onSaved={(saved, isCreate) => {
          enqueueSnackbar(t(isCreate ? 'toast.created' : 'toast.updated'), {
            variant: 'success',
          });
          setSelectedId(saved.id);
          setEditorId(null);
        }}
        onDeleted={() => {
          enqueueSnackbar(t('toast.deleted'), { variant: 'success' });
          setSelectedId(null);
          setEditorId(null);
        }}
      />
    </Box>
  );
};
